import React, { useEffect, useMemo, useRef, useState } from 'react';
import './WordleGame.css';
import './PatternQuizGame.css';
import { PointsStarIcon } from './WordleGame';
import { s3PublicUrl } from '../../utils/s3PublicUrl';
import DayQuestionPanel from './DayQuestionPanel';
import { useSelector } from 'react-redux';
import { demoGatePopupAndRedirect, isUserDemoBypass } from '../../utils/demoGate';

export function answersMatchPattern(userInput, expected) {
  const u = String(userInput ?? '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();
  const e = String(expected ?? '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();
  return u === e;
}

/**
 * Pattern / numeric quiz: theme questions with `question`, `answer`, `did_you_know`.
 * Same chrome as Day 1 (toolbar, badge, points).
 */
export default function PatternQuizGame({
  questions,
  rules,
  badgeUrl,
  badgeTitle = '',
  questionImage,
  didYouKnowImage,
  questionTextWrapClassName = '',
  questionTextClassName = '',
  correctPoints,
  dayBonusPoints,
  pointsIconUrl,
  badgeFallbackLabel = 'Day 2',
  onComplete,
}) {
  const user = useSelector((state) => state.auth?.user);
  const demoPromoScheduledRef = useRef(false);
  const list = useMemo(() => (Array.isArray(questions) ? questions : []), [questions]);
  const rulesList = Array.isArray(rules) ? rules : [];

  const [questionIndex, setQuestionIndex] = useState(0);
  const [input, setInput] = useState('');
  const [showRules, setShowRules] = useState(false);
  const [showDidYouKnow, setShowDidYouKnow] = useState(false);
  const [dykText, setDykText] = useState('');
  const [revealAnswer, setRevealAnswer] = useState('');
  const [earnedPoints, setEarnedPoints] = useState(0);
  const [submittedTotalPoints, setSubmittedTotalPoints] = useState(0);
  const [message, setMessage] = useState('');
  const [inputShake, setInputShake] = useState(false);
  const [badgeImageFailed, setBadgeImageFailed] = useState(false);
  const [gameComplete, setGameComplete] = useState(false);
  const [allCorrectSoFar, setAllCorrectSoFar] = useState(true);
  const [lastWasCorrect, setLastWasCorrect] = useState(false);
  const [pointsToast, setPointsToast] = useState(null);
  const completeSentRef = useRef(false);

  const pointsAward = useMemo(() => {
    const n = Number(correctPoints);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  }, [correctPoints]);

  const dayBonusAward = useMemo(() => {
    const n = Number(dayBonusPoints);
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
  }, [dayBonusPoints]);

  const current = list[questionIndex];
  const questionImageUrl = s3PublicUrl(questionImage);
  const didYouKnowImageUrl = s3PublicUrl(didYouKnowImage);

  useEffect(() => {
    setBadgeImageFailed(false);
  }, [badgeUrl]);

  const triggerShake = () => {
    setInputShake(true);
    window.setTimeout(() => setInputShake(false), 500);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (gameComplete || !current) return;
    if (isUserDemoBypass(user)) {
      if (!demoPromoScheduledRef.current) {
        demoPromoScheduledRef.current = true;
        demoGatePopupAndRedirect();
      }
      return;
    }
    if (String(input ?? '').trim() === '') {
      triggerShake();
      setMessage('Please enter an answer');
      return;
    }

    const correct = answersMatchPattern(input, current.answer);
    setLastWasCorrect(correct);
    if (!correct) setAllCorrectSoFar(false);
    const nextTotalPoints = earnedPoints + (correct ? pointsAward : 0);
    setMessage('');
    setSubmittedTotalPoints(nextTotalPoints);
    if (correct) {
      setEarnedPoints(nextTotalPoints);
      setRevealAnswer('');
    } else {
      triggerShake();
      setRevealAnswer(String(current.answer ?? ''));
    }
    setDykText(String(current.did_you_know ?? ''));
    setShowDidYouKnow(true);
  };

  const handleCloseDyk = () => {
    setShowDidYouKnow(false);
    const next = questionIndex + 1;
    if (next >= list.length) {
      const allCorrect = Boolean(allCorrectSoFar && lastWasCorrect && list.length > 0);
      const finalPoints = submittedTotalPoints + (allCorrect ? dayBonusAward : 0);
      if (allCorrect && dayBonusAward > 0) {
        setEarnedPoints(finalPoints);
        setPointsToast(`+${dayBonusAward}`);
        window.setTimeout(() => setPointsToast(null), 2600);
      }
      if (typeof onComplete === 'function') {
        if (!completeSentRef.current) {
          completeSentRef.current = true;
          onComplete({ earnedPoints: finalPoints, answer: 'completed', allCorrect });
        }
      }
      return;
    }
    setQuestionIndex(next);
    setSubmittedTotalPoints(earnedPoints);
    setInput('');
    setMessage('');
  };

  if (!list.length) {
    return (
      <p className="wordle-notification" style={{ textAlign: 'center' }}>
        No questions configured for this stage.
      </p>
    );
  }

  return (
    <div className="wordle-shell">
      <div className="wordle-toolbar-strip">
        <div className="wordle-toolbar-end">
          {rulesList.length > 0 ? (
            <button
              type="button"
              className="wordle-icon-btn wordle-icon-rules"
              onClick={() => setShowRules(true)}
              aria-label="How to play"
              title="How to play"
            >
              ?
            </button>
          ) : null}
          <div className="wordle-points-group" title="Points">
            <PointsStarIcon pointsIconUrl={pointsIconUrl} />
            <span className="wordle-points-value">{earnedPoints}</span>
            {pointsToast ? <span className="wordle-points-toast">{pointsToast}</span> : null}
          </div>
        </div>
      </div>

      <div className="wordle-root">
        <div className="wordle-badge-header">
          <div className="wordle-badge-header__logo">
            {badgeUrl && !badgeImageFailed ? (
              <img
                src={badgeUrl}
                alt=""
                className="wordle-badge-img"
                onError={() => setBadgeImageFailed(true)}
              />
            ) : (
              <div className="wordle-badge-fallback" aria-hidden>
                {badgeFallbackLabel}
              </div>
            )}
          </div>
          <div className="wordle-badge-header__title">
            <span className="wordle-badge-title">
              {String(badgeTitle || '').trim() || badgeFallbackLabel}
            </span>
          </div>
        </div>

        {!gameComplete && current ? (
          <form className="pattern-quiz-form" onSubmit={handleSubmit}>
            {questionImageUrl ? (
              <DayQuestionPanel
                imageUrl={questionImageUrl}
                question={current.question}
                textWrapClassName={questionTextWrapClassName}
                textClassName={questionTextClassName}
              />
            ) : (
              <p className="pattern-quiz-question">{current.question}</p>
            )}
            <input
              type="text"
              className={`pattern-quiz-input ${inputShake ? 'pattern-quiz-input--shake' : ''}`}
              value={input}
              onChange={(ev) => {
                setInput(ev.target.value);
                if (message) setMessage('');
              }}
              placeholder="Your answer"
              autoComplete="off"
              required
              disabled={gameComplete}
              aria-label="Answer"
            />
            <button
              type="submit"
              className="pattern-quiz-submit"
              disabled={gameComplete || String(input ?? '').trim() === ''}
            >
              Submit
            </button>
          </form>
        ) : null}

        {gameComplete ? (
          <p className="pattern-quiz-complete">{message}</p>
        ) : (
          <div className="wordle-notification pattern-quiz-hint">{message}</div>
        )}

        {showRules && rulesList.length > 0 && (
          <div
            className="wordle-modal-overlay"
            role="dialog"
            aria-modal="true"
            aria-labelledby="pattern-rules-title"
          >
            <div className="wordle-modal">
              <h2 id="pattern-rules-title">How to play</h2>
              <ul className="wordle-rules-list">
                {rulesList.map((line, i) => (
                  <li key={i}>{line}</li>
                ))}
              </ul>
              <div className="wordle-modal-actions">
                <button type="button" onClick={() => setShowRules(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {showDidYouKnow && (
          <div
            className="wordle-modal-overlay wordle-modal-overlay--dyk"
            role="dialog"
            aria-modal="true"
            aria-label="Answer details"
          >
            <div className="wordle-modal wordle-modal--dyk">
              {didYouKnowImageUrl ? (
                <img src={didYouKnowImageUrl} alt="" className="dyk-banner-image" />
              ) : (
                <div className="dyk-banner-fallback">Did you know?</div>
              )}

              <div className="dyk-body-box">
                {revealAnswer ? (
                  <p className="mcq-reveal dyk-reveal">
                  Correct Answer: <strong>{revealAnswer}</strong>
                  </p>
                ) : null}
                {dykText ? <p className="dyk-text">{dykText}</p> : null}
              </div>

              <div className="wordle-modal-actions wordle-modal-actions--center">
                <button type="button" onClick={handleCloseDyk}>
                  OK
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
